import type { ColumnDef, HeaderContext } from '@tanstack/react-table';
import styled from 'styled-components';

import { GetDeliveryQuery } from 'data/__generated__';
import { textColumn, numberColumn } from 'components/DataTable';

export type DeliveryProductSummaryRow = NonNullable<GetDeliveryQuery['delivery']>['ordersProductSummary'][number];

type ProductSummaryFilter = { searchValue?: string };

type NumericKey = 'quantityRequested' | 'quantityPositioned' | 'quantitySoldoutOnline' | 'quantityPicked';

// Sum a numeric column over the currently displayed rows (respects the search filter).
const sumColumn = (key: NumericKey, { table }: HeaderContext<DeliveryProductSummaryRow, unknown>): number =>
    table.getFilteredRowModel().rows.reduce((acc, row) => acc + row.original[key], 0);

export const productSummaryColumns: ColumnDef<DeliveryProductSummaryRow, unknown>[] = [
    {
        ...textColumn<DeliveryProductSummaryRow, ProductSummaryFilter>({
            accessorKey: 'fullname',
            header: 'Produit',
            defaultVisible: true,
        }),
        footer: () => <TotalValue>Total</TotalValue>,
    },
    {
        ...numberColumn<DeliveryProductSummaryRow, ProductSummaryFilter>({
            accessorKey: 'quantityRequested',
            header: 'Demandé',
            defaultVisible: true,
        }),
        footer: (ctx) => <TotalValue>{sumColumn('quantityRequested', ctx)}</TotalValue>,
    },
    {
        ...numberColumn<DeliveryProductSummaryRow, ProductSummaryFilter>({
            accessorKey: 'quantityPositioned',
            header: 'Positionné',
            defaultVisible: true,
        }),
        footer: (ctx) => <TotalValue>{sumColumn('quantityPositioned', ctx)}</TotalValue>,
    },
    {
        ...numberColumn<DeliveryProductSummaryRow, ProductSummaryFilter>({
            accessorKey: 'quantitySoldoutOnline',
            header: 'Soldout online',
            defaultVisible: true,
            cell: ({ row }) => (
                <SoldoutCell $hasSoldout={row.original.quantitySoldoutOnline > 0}>
                    {row.original.quantitySoldoutOnline}
                </SoldoutCell>
            ),
        }),
        footer: (ctx) => {
            const total = sumColumn('quantitySoldoutOnline', ctx);
            return <TotalSoldoutValue $hasSoldout={total > 0}>{total}</TotalSoldoutValue>;
        },
    },
    {
        ...numberColumn<DeliveryProductSummaryRow, ProductSummaryFilter>({
            accessorKey: 'quantityPicked',
            header: 'Préparé',
            defaultVisible: true,
        }),
        footer: (ctx) => <TotalValue>{sumColumn('quantityPicked', ctx)}</TotalValue>,
    },
    {
        ...numberColumn<DeliveryProductSummaryRow, ProductSummaryFilter>({
            accessorKey: 'quantitySoldoutBundle',
            header: 'Soldout bundle',
            defaultVisible: true,
            // null until preparation is complete (quantityPicked set on every box) → show "—".
            cell: ({ row }) => {
                const soldoutBundle = row.original.quantitySoldoutBundle;
                if (soldoutBundle === null || soldoutBundle === undefined) {
                    return <span>—</span>;
                }
                return <SoldoutCell $hasSoldout={soldoutBundle > 0}>{soldoutBundle}</SoldoutCell>;
            },
        }),
        // Sum nulls as 0, but keep "—" while the whole recap is still unprepared (every row null).
        footer: ({ table }) => {
            const rows = table.getFilteredRowModel().rows;
            if (rows.every((row) => row.original.quantitySoldoutBundle == null)) {
                return <span>—</span>;
            }
            const total = rows.reduce((acc, row) => acc + (row.original.quantitySoldoutBundle ?? 0), 0);
            return <TotalSoldoutValue $hasSoldout={total > 0}>{total}</TotalSoldoutValue>;
        },
    },
];

// Highlights the soldout columns (red/bold) when there is a gap, like the order recap's mismatch styling.
const SoldoutCell = styled.span<{ $hasSoldout: boolean }>`
    ${({ $hasSoldout, theme }) => $hasSoldout && `color: ${theme.errorColor};`}
    font-weight: ${({ $hasSoldout }) => ($hasSoldout ? 800 : 500)};
`;

// Totals row (table footer): emphasised values, soldout totals stay red when non-zero.
const TotalValue = styled.span`
    font-weight: 700;
`;

const TotalSoldoutValue = styled.span<{ $hasSoldout: boolean }>`
    font-weight: 700;
    ${({ $hasSoldout, theme }) => $hasSoldout && `color: ${theme.errorColor};`}
`;
